import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CustomerAppInstall from '../components/customer/CustomerAppInstall';
import { homeForRole } from '../utils/homeForRole';
import { configAPI } from '../services/api';
import { IS_DEMO, IS_LAXMI_HOST, DEMO_LOGINS, DEMO_PASSWORD } from '../utils/demo';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Phone, Lock, LogIn, Users, Tv, Wallet, User } from 'lucide-react';

// One sign-in page for every role. /login, /agent/login and /portal/login all render this; only the
// words differ per entry point. The server works out the role from mobile + password (POST /auth/login)
// and homeForRole sends each person to their own area, so any role can sign in at any of the three.
export const VARIANTS = {
  staff: { title: ['Welcome', 'Back'], sub: 'Sign in to your account', badge: 'Network & Billing Management', Icon: Users },
  agent: { title: ['Agent', 'Login'], sub: 'Sign in to start collecting', badge: 'Collection Agent', Icon: Wallet },
  customer: { title: ['Welcome', 'Back'], sub: 'Sign in to manage your connection', badge: 'Customer Portal', Icon: User },
};

export default function LoginPage({ variant = 'staff' }) {
  const v = VARIANTS[variant] || VARIANTS.staff;
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ mobile: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [brand, setBrand] = useState({ name: '', logo: '', phone: '' });

  // Same brand source as the agent/customer portal logins — company name/logo/phone come
  // from Settings, so rebranding never needs a code change here.
  useEffect(() => {
    configAPI.get().then(({ data }) => {
      const d = data?.data;
      if (!d) return;
      const identity = d.catv?.name ? d.catv : d.broadband?.name ? d.broadband : null;
      setBrand({
        name: identity?.name || d.companyName || '',
        logo: identity?.logo || '',
        phone: identity?.phone || d.companyPhone || '',
      });
    }).catch(() => {});
  }, []);

  // No more "Login As" — the role is no longer picked here at all. POST /auth/login now
  // resolves it from mobile+password alone (User.mobile is unique across every role, so the
  // lookup is never ambiguous) and the response says which one it landed on; homeForRole
  // sends the user straight to that role's home instead of always assuming /dashboard.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(form);
      toast.success(`Welcome, ${data.user.name}!`);
      navigate(homeForRole(data.user.role));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="crm-login">
      <style>{`
        .crm-login {
          min-height: 100vh; display: flex; align-items: center; justify-content: center;
          padding: calc(32px + env(safe-area-inset-top, 0px)) 20px calc(28px + env(safe-area-inset-bottom, 0px));
          background: radial-gradient(circle at 50% 0%, #1e3a8a 0%, #0c1a3d 55%, #060e24 100%);
        }
        .crm-login-card { width: 100%; max-width: 400px; }
      `}</style>

      <div className="crm-login-card">
        {/* Brand header — company logo + name from Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 22 }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%', overflow: 'hidden',
            border: '3px solid rgba(255,255,255,.35)', boxShadow: '0 8px 28px rgba(0,0,0,.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c1a3d', flexShrink: 0,
          }}>
            {brand.logo
              ? <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Tv size={38} color="#fff" />}
          </div>

          <h1 style={{
            color: '#fff', fontSize: '1.55rem', fontWeight: 900, letterSpacing: '.03em',
            margin: '14px 0 4px', textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.2,
          }}>
            {brand.name || ' '}
          </h1>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.22)',
            borderRadius: 999, padding: '5px 14px',
            color: '#fff', fontSize: '.7rem', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase',
          }}>
            <v.Icon size={13} /> {v.badge}
          </div>
        </div>

        {/* Login form */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: '28px 24px',
          boxShadow: '0 8px 30px rgba(0,0,0,.25)', border: '1px solid #e5e7eb',
        }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: 2, color: '#111827' }}>
            {v.title[0]} <span style={{ color: 'var(--primary)' }}>{v.title[1]}</span>
          </h2>
          <p style={{ fontSize: '.84rem', color: '#6b7280', marginBottom: 4 }}>{v.sub}</p>
          <div style={{ width: 40, height: 3, background: 'var(--primary)', borderRadius: 2, marginBottom: 20 }} />

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              <div style={{ position: 'relative' }}>
                <Phone size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="tel" inputMode="numeric" className="form-control"
                  placeholder="Enter mobile number"
                  value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}
                  style={{ paddingLeft: 36 }} required autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type={showPass ? 'text' : 'password'} className="form-control"
                  placeholder="Enter password"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  style={{ paddingLeft: 36, paddingRight: 40 }} required
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ width: '100%', justifyContent: 'center', gap: 8, padding: '12px', fontSize: '.92rem', marginTop: 10 }}>
              <LogIn size={16} /> {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {IS_DEMO && (
            <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: 'var(--cb-eff6ff, #eff6ff)', border: '1px solid var(--cb-bfdbfe, #bfdbfe)' }}>
              <div style={{ fontSize: '.8rem', fontWeight: 700, marginBottom: 8, color: '#1e3a8a' }}>
                Demo logins (password {DEMO_PASSWORD}). Tap one to fill it in.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {DEMO_LOGINS.map(l => (
                  <button key={l.mobile} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setForm({ mobile: l.mobile, password: DEMO_PASSWORD })}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variant === 'customer' && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Link to="/portal/register" style={{ fontSize: '.85rem', color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
                New here? Get a new connection
              </Link>
              <div><CustomerAppInstall link /></div>
            </div>
          )}

          <p style={{ marginTop: 16, textAlign: 'center', fontSize: '.78rem', color: '#6b7280' }}>
            Forgot your password?{' '}
            {brand.phone
              ? <a href={`tel:${brand.phone}`} style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>Call {brand.phone}</a>
              : 'Contact the office.'}
          </p>
        </div>

        {/* Authorised-operator mark: Laxmi's own customer login only. Never shown on the demo or an operator's copy. */}
        {variant === 'customer' && IS_LAXMI_HOST && (
          <div style={{ marginTop: 18, background: '#fff', borderRadius: 14, padding: '10px 14px', textAlign: 'center' }}>
            <img src="/brand/gtpl-authorized.png" alt="Authorized GTPL Local Cable Operator" style={{ width: '100%', maxWidth: 240, display: 'block', margin: '0 auto' }} />
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 22, textAlign: 'center', color: 'rgba(255,255,255,.5)', fontSize: '.72rem', lineHeight: 1.9 }}>
          <div>© {new Date().getFullYear()} {brand.name}. All Rights Reserved.</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/portal/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>
            <Link to="/portal/refund-policy" style={{ color: 'inherit', textDecoration: 'underline' }}>Refund Policy</Link>
            <Link to="/portal/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms &amp; Conditions</Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 2, opacity: .8 }}>
            Powered by <strong style={{ fontWeight: 700 }}>Orvyn</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
